import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import {
  useDisputeByIdRelayQuery,
  mapDisputeToIDispute,
} from '../../hooks/transaction/query/V2';
import { formatPhoneNumber, getLocaleDateTimeString } from '../../utils';
import PageContainer from '../../components/Redesing/page-container';
import PageContent from '../../components/Redesing/page-content';
import CardView from '../../components/Redesing/card-view';
import { Stack } from '@mui/system';
import { PropertyList } from '../../components/Redesing/property-list';
import { PropertyListItem } from '../../components/Redesing/property-list-item';
import PropertyListItemLink from '../../components/Redesing/property-list-item-link';
import { SeverityPill } from '../../components/Redesing/severity-pill';
import DetailPageSkeleton from '../../components/Redesing/skeleton/DetailPageSkeleton';

const ATTENTION_STATUSES = [
  'Needs Response',
  'Under Review',
  'Warning Under Review',
  'Warning Needs Response',
];

const statusPillColor = (status: string) => {
  if (status === 'Won') return 'success';
  if (status === 'Lost') return 'error';
  if (ATTENTION_STATUSES.includes(status)) return 'warning';
  return 'info';
};

const DisputeDetails = ({ systemLoaded }: { systemLoaded: boolean }) => {
  const { disputeId } = useParams<{ disputeId: string }>();
  const { data: disputeData, isFetched } = useDisputeByIdRelayQuery(disputeId, {
    enabled: systemLoaded,
  });
  const dispute = useMemo(
    () => (disputeData ? mapDisputeToIDispute(disputeData) : null),
    [disputeData]
  );

  if (!isFetched || !systemLoaded) {
    return (
      <PageContainer>
        <DetailPageSkeleton
          cards={[
            { rows: 8, title: true },
            { rows: 3, title: true },
          ]}
        />
      </PageContainer>
    );
  }

  if (!dispute) {
    return <Redirect to="/billing/disputes" />;
  }

  const warning = ATTENTION_STATUSES.includes(dispute.status)
    ? 'Please contact user right away to find out why they have disputed this charge, then contact Movatic at support@movatic.co'
    : null;

  const generalInfo = (
    <PropertyList dataId="general-info-dispute">
      {dispute.charge_id && (
        <PropertyListItemLink
          divider
          label="Charge ID"
          dataId="charge-id"
          to={`/billing/transactions/detail/${dispute.charge_id}`}
          text={dispute.charge_id}
        />
      )}
      <PropertyListItem
        divider
        label="Original Charge Amount"
        value={dispute.amount}
        dataId="dispute-amount"
      />
      <PropertyListItem
        divider
        label="Dispute Fee"
        value={dispute.fee}
        dataId="dispute-fee"
      />
      <PropertyListItem divider label="Net" value={dispute.net} dataId="dispute-net" />
      <PropertyListItem
        divider
        label="Dispute Date"
        value={getLocaleDateTimeString(dispute.created)}
        dataId="dispute-date"
      />
      {dispute.evidence_duedate && (
        <PropertyListItem
          divider
          label="Respond By"
          value={getLocaleDateTimeString(dispute.evidence_duedate)}
          dataId="dispute-evidence"
        />
      )}
      <PropertyListItem
        divider
        label="Reason"
        value={dispute.reason}
        dataId="dispute-reason"
      />
      <PropertyListItem label="Status" dataId="dispute-status">
        <SeverityPill color={statusPillColor(dispute.status)}>
          {dispute.status}
        </SeverityPill>
      </PropertyListItem>
    </PropertyList>
  );

  const userInfo = (
    <PropertyList dataId="user-info-dispute">
      <PropertyListItemLink
        divider
        label="User"
        dataId="dispute-username"
        to={`/users/${dispute.phone}/general`}
        text={dispute.userName}
      />
      <PropertyListItem
        divider
        label="Phone Number"
        value={formatPhoneNumber(dispute.phone)}
        dataId="dispute-phone"
      />
      <PropertyListItem
        label="Email Address"
        value={dispute.email}
        dataId="dispute-email"
      />
    </PropertyList>
  );

  return (
    <PageContainer warningMessage={warning}>
      <PageContent
        dataId="dispute-details"
        backLink="/billing/disputes"
        contentTitle="Disputes"
        itemName="Dispute"
        itemChipValue={dispute.id}
        chipLabel="Dispute ID"
        tabsChildren={
          <Stack spacing={2} data-id="dispute-details-content">
            <CardView
              dataId="dispute-general-info"
              content={<Stack>{generalInfo}</Stack>}
              title="General Information"
            />
            <CardView
              dataId="dispute-user-info"
              content={<Stack>{userInfo}</Stack>}
              title="User Information"
            />
          </Stack>
        }
      />
    </PageContainer>
  );
};

export default connect(
  (state: { system: { isLoaded: boolean } }) => ({
    systemLoaded: state.system.isLoaded,
  }),
  () => ({})
)(DisputeDetails);
